import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { ConnectedPosition, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { CdkListbox, CdkOption } from "@angular/cdk/listbox";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-light-svg-icons";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { DropdownType } from "../../models/dropdown-type.enum";
import { faCircleUser } from "@fortawesome/pro-regular-svg-icons";
import { Subscription } from "rxjs";
import { Option } from "../../models/option.model";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";

@Component({
  selector: "dropdown",
  templateUrl: "./dropdown.component.html",
  styleUrls: ["./dropdown.component.css"],
  imports: [FormsModule, CommonModule, CdkListbox, CdkOption, FaIconComponent],
  standalone: true
})
export class DropdownComponent implements OnDestroy {
  @Input() selectedOption: Option | null = null;
  @Input() options: Option[] = [];
  @Input() dropdownType: DropdownType = DropdownType.Date;
  @Input() position: ConnectedPosition = {
    originX: "start",
    originY: "bottom",
    overlayX: "start",
    overlayY: "top"
  };
  @ViewChild("dropdownTemplate") dropdownTemplate!: TemplateRef<any>;
  @ViewChild("trigger") trigger!: ElementRef;
  @Output() optionSelectedEvent: EventEmitter<Option> = new EventEmitter<Option>();
  private overlayRef!: OverlayRef;
  private backdropClickSubscription!: Subscription;
  protected readonly faAngleDown: IconDefinition = faAngleDown;
  protected readonly faChevronDown: IconDefinition = faChevronDown;
  protected readonly faChevronUp: IconDefinition = faChevronUp;
  protected readonly faAngleUp: IconDefinition = faAngleUp;
  protected readonly DropdownType: typeof DropdownType = DropdownType;
  protected readonly faCircleUser: IconDefinition = faCircleUser;
  protected filteredOptions: Option[] = [];
  protected isDropdownOpen: boolean = false;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnDestroy(): void {
    if (this.backdropClickSubscription) {
      this.backdropClickSubscription.unsubscribe();
    }
  }

  openDropdown(): void {
    if (this.isDropdownOpen) {
      this.closeDropdown();
      return;
    }
    if (this.overlayRef == null || !this.overlayRef.hasAttached()) {
      this.filteredOptions = this.options;
      this.overlayRef = this.overlay.create({
        positionStrategy: this.overlay
          .position()
          .flexibleConnectedTo(this.trigger)
          .withPush(true)
          .withPositions([this.position]),
        scrollStrategy: this.overlay.scrollStrategies.close(),
        hasBackdrop: true,
        backdropClass: "cdk-overlay-transparent-backdrop"
      });
      const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
      this.overlayRef.attach(portal);
      this.backdropClickSubscription = this.overlayRef.backdropClick().subscribe((): void => {
        this.closeDropdown();
        this.isDropdownOpen = false;
      });
      this.isDropdownOpen = true;
    }
  }

  closeDropdown(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
    this.isDropdownOpen = false;
  }

  emitSelectedOption(option: Option): void {
    this.selectedOption = option;
    this.optionSelectedEvent.emit(option);
  }

  optionSelected(option: Option): void {
    this.selectedOption = option;
    this.emitSelectedOption(option);
    this.closeDropdown(); // Close dropdown after selection
  }
}
